import { isHiddenMarker } from '../../config/constants';
import type { ConversationPair } from '../../services/chatStorage';
import { logger } from '../core/logger';

const TURN_SEPARATOR = '\n\n---\n\n';

/**
 * Serialize a conversation to plain text for the clipboard (IX-4266).
 *
 * Applies the same visibility rule the transcript renders by, so synthetic
 * protocol turns never leak, and appends the in-flight answer, which the
 * visitor can already see but which is not in `conversationPairs` yet.
 */
export const buildTranscript = (
    conversationPairs: ConversationPair[],
    streamingContent?: string
): string => {
    const turns = conversationPairs
        .map((pair) => {
            const question = isHiddenMarker(pair.question?.content)
                ? null
                : `> ${pair.question?.content ?? ''}`;
            const answer = pair.answer?.content ?? null;
            return [question, answer].filter(Boolean).join('\n\n');
        })
        .filter(Boolean);

    if (streamingContent) {
        turns.push(streamingContent);
    }

    return turns.join(TURN_SEPARATOR);
};

/**
 * Write the transcript to the clipboard, reporting whether it actually landed.
 *
 * The Clipboard API is absent on non-secure origins and in some webviews.
 * Optional chaining on `writeText` would resolve to undefined without throwing,
 * which reads as success and produces a "Copied" acknowledgement for a copy
 * that never happened — so the capability is checked explicitly.
 */
export const copyTranscript = async (
    conversationPairs: ConversationPair[],
    streamingContent: string | undefined,
    clipboard: Pick<Clipboard, 'writeText'> | undefined
): Promise<boolean> => {
    const transcript = buildTranscript(conversationPairs, streamingContent);
    if (!transcript) return false;

    if (typeof clipboard?.writeText !== 'function') {
        logger.warn('Transcript: Clipboard API unavailable, cannot copy');
        return false;
    }

    try {
        await clipboard.writeText(transcript);
        return true;
    } catch (error) {
        logger.warn('Transcript: clipboard write failed', error);
        return false;
    }
};
